import { useState, useEffect } from 'react'
import { Download, X, Share, TrendingUp } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showBanner, setShowBanner] = useState(false)
  const [showIOSGuide, setShowIOSGuide] = useState(false)

  const isIOS = typeof navigator !== 'undefined' && (
    /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )

  useEffect(() => {
    // Don't show if already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) return

    // Check if dismissed recently (7 days)
    const dismissed = localStorage.getItem('pwa-install-dismissed')
    if (dismissed && Date.now() - Number(dismissed) < 7 * 24 * 60 * 60 * 1000) return

    if (isIOS) {
      const timer = setTimeout(() => setShowBanner(true), 3000)
      return () => clearTimeout(timer)
    }

    // Android / Desktop: listen for beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setTimeout(() => setShowBanner(true), 2000)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [isIOS])

  const handleInstall = async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      setShowBanner(false)
    }
    setDeferredPrompt(null)
  }

  const handleDismiss = () => {
    setShowBanner(false)
    setShowIOSGuide(false)
    localStorage.setItem('pwa-install-dismissed', String(Date.now()))
  }

  if (!showBanner) return null

  // iOS guide — full-screen page styled like AgentPage empty state
  if (isIOS && showIOSGuide) {
    return (
      <div
        className="fixed inset-0 z-50 bg-bg animate-fade-in"
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <header
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0 16px', height: 48, flexShrink: 0,
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div style={{ width: 38 }} />
          <h1 style={{ fontSize: 16, fontWeight: 600 }} className="text-text-primary">安装引导</h1>
          <button
            onClick={handleDismiss}
            style={{ padding: 8, marginRight: -8, display: 'flex' }}
            className="rounded-lg hover:bg-surface text-text-muted cursor-pointer transition-colors duration-150"
          >
            <X style={{ width: 22, height: 22 }} />
          </button>
        </header>

        {/* Content — centered like AgentPage empty state */}
        <div
          style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            padding: '0 24px', textAlign: 'center',
          }}
        >
          {/* Hero Icon */}
          <div
            style={{ width: 64, height: 64, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}
            className="bg-primary/10"
          >
            <TrendingUp style={{ width: 36, height: 36 }} className="text-primary" />
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }} className="text-text-primary">
            添加到主屏幕
          </h2>
          <p style={{ fontSize: 14, marginBottom: 32, maxWidth: 280 }} className="text-text-muted">
            将 VibeAlpha 添加到主屏幕，获得原生 App 体验
          </p>

          {/* Steps */}
          <div style={{ width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 20, textAlign: 'left' }}>
            {[
              { step: '1', text: <>点击 Safari 底部的 <Share size={14} className="inline text-primary" style={{ verticalAlign: -2 }} /> 分享按钮</> },
              { step: '2', text: '向下滚动，选择「添加到主屏幕」' },
              { step: '3', text: '点击右上角「添加」即可完成' },
            ].map(({ step, text }) => (
              <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span
                  style={{
                    width: 32, height: 32, borderRadius: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, fontWeight: 600, flexShrink: 0,
                  }}
                  className="bg-primary/10 text-primary"
                >
                  {step}
                </span>
                <span style={{ fontSize: 14 }} className="text-text-secondary">{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom action */}
        <div style={{ padding: '12px 24px', paddingBottom: 32, flexShrink: 0 }}>
          <button
            onClick={handleDismiss}
            style={{ width: '100%', height: 48, fontSize: 14, fontWeight: 500 }}
            className="rounded-lg bg-surface border border-border text-text-secondary
                       cursor-pointer active:scale-[0.97] transition-all duration-150"
          >
            我知道了
          </button>
        </div>
      </div>
    )
  }

  // Install banner
  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-slide-up">
      <div
        className="max-w-md mx-auto bg-surface border border-border shadow-xl"
        style={{ borderRadius: 16, padding: 12, paddingLeft: 16 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{ width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            className="bg-primary/10"
          >
            <Download style={{ width: 20, height: 20 }} className="text-primary" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.3 }} className="text-text-primary">安装 VibeAlpha</p>
            <p style={{ fontSize: 12, lineHeight: 1.3 }} className="text-text-muted">添加到主屏幕，获得原生体验</p>
          </div>
          <button
            onClick={handleDismiss}
            style={{ padding: 6, flexShrink: 0 }}
            className="rounded-lg hover:bg-elevated text-text-muted cursor-pointer transition-colors duration-150"
          >
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            onClick={isIOS ? () => setShowIOSGuide(true) : handleInstall}
            style={{ flex: 1, height: 40, fontSize: 14, fontWeight: 500 }}
            className="rounded-lg bg-primary text-on-primary
                       cursor-pointer active:scale-[0.97] transition-all duration-150"
          >
            安装
          </button>
          <button
            onClick={handleDismiss}
            style={{ width: 80, height: 40, fontSize: 14 }}
            className="rounded-lg bg-elevated text-text-muted
                       cursor-pointer active:scale-[0.97] transition-all duration-150"
          >
            稍后
          </button>
        </div>
      </div>
    </div>
  )
}
